import { Context, createContext, FC, useCallback, useMemo } from 'react';
import { trace, ROOT_CONTEXT, Context as Ctx } from '@opentelemetry/api';

import { MonitoringSpanEnum } from '@src/core/monitoring/monitoring-span.enum';
import { PageLoadingSpanAttribute, PageLoadingTracerName } from '@src/core/monitoring/constants';
import { MonitoringGroupsEnum } from '@src/core/monitoring/monitoring-groups.enum';

export interface IMonitoringContext {
  registerMonitorMeasure: (name: MonitoringSpanEnum) => void;
  stopMonitorMeasure: (name: MonitoringSpanEnum) => void;
  deleteGroupOfMeasurements: (groupName: MonitoringGroupsEnum) => void;
}

export const MonitoringContext: Context<IMonitoringContext | null> = createContext(null);

export const MonitoringProvider: FC = ({ children }) => {
  const spans = useMemo(() => new Map<MonitoringSpanEnum, Ctx>(), []);

  const stopMonitorMeasure = useCallback(
    (name: MonitoringSpanEnum) => {
      if (!spans.has(name)) {
        return;
      }

      const spanContext = spans.get(name);
      trace.getSpan(spanContext).end();
      spans.delete(name);
    },
    [spans]
  );

  const deleteGroupOfMeasurements = useCallback(
    (groupName: MonitoringGroupsEnum) => {
      Array.from(spans.keys()).forEach((spanName) => {
        if (spanName.includes(groupName)) {
          const spanContext = spans.get(spanName);
          trace.deleteSpan(spanContext);
          spans.delete(spanName);
        }
      });
    },
    [spans]
  );

  const registerMonitorMeasure = useCallback(
    (name: MonitoringSpanEnum) => {
      if (spans.has(name)) {
        stopMonitorMeasure(name);
        return;
      }
      const tracer = trace.getTracer(PageLoadingTracerName);
      const span = tracer.startSpan(name, {
        attributes: {
          [PageLoadingSpanAttribute]: name,
        },
      });
      const activeSpanContext = trace.setSpan(ROOT_CONTEXT, span);
      spans.set(name, activeSpanContext);
    },
    [spans, stopMonitorMeasure]
  );

  return (
    <MonitoringContext.Provider
      value={{
        registerMonitorMeasure,
        stopMonitorMeasure,
        deleteGroupOfMeasurements,
      }}
    >
      {children}
    </MonitoringContext.Provider>
  );
};
